'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var solidJs = require('solid-js');
var regexparam = require('regexparam');
var web = require('solid-js/web');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var regexparam__default = /*#__PURE__*/_interopDefaultLegacy(regexparam);

const hasSchemeRegex = /^(?:[a-z0-9]+:)?\/\//i;
const normalizeRegex = /^\/+|\/+$|\s+/;

function normalize(path) {
  const s = path.replace(normalizeRegex, '');
  return s ? '/' + s : '';
}

function resolvePath(base, path, from) {
  if (hasSchemeRegex.test(path)) {
    return undefined;
  }

  const basePath = normalize(base);
  const fromPath = from && normalize(from);
  let result = '';

  if (!fromPath || path.charAt(0) === '/') {
    result = basePath;
  } else if (fromPath.toLowerCase().indexOf(basePath.toLowerCase()) !== 0) {
    result = basePath + fromPath;
  } else {
    result = fromPath;
  }

  return result + normalize(path) || '/';
}
function createMatcher(path, options) {
  const {
    keys,
    pattern
  } = regexparam__default['default'](path, !options.end);
  return p => {
    const matches = pattern.exec(p);

    if (!matches) {
      return null;
    }

    const params = keys.reduce((acc, _, i) => {
      acc[keys[i]] = matches[i + 1];
      return acc;
    }, {});
    return [matches[0] || '/', params];
  };
}
function parseQuery(queryString) {
  return queryString.split('&').reduce((acc, pair) => {
    const [key, value] = pair.split('=', 2);

    if (key) {
      acc[key.toLowerCase()] = value;
    }

    return acc;
  }, {});
}
function renderPath(path) {
  return path;
}

const MAX_REDIRECTS = 100;
const RouterContext = solidJs.createContext();
const RouteContext = solidJs.createContext();
const useRouter = () => {
  const router = solidJs.useContext(RouterContext);

  if (!router) {
    throw new Error('No router context defined - ensure your application is wrapped with a Router component');
  }

  return router;
};
const useRoute = () => solidJs.useContext(RouteContext) || useRouter().base;
const defaultUtils = {
  resolvePath,
  createMatcher,
  parseQuery,
  renderPath
};

function normalizeIntegration(integration) {
  if (!integration) {
    return {
      signal: solidJs.createSignal({
        value: ''
      })
    };
  } else if (Array.isArray(integration)) {
    return {
      signal: integration
    };
  }

  return integration;
}

function createRouter(integration, basePath = '', overrides) {
  const {
    signal: [source, setSource],
    utils: intUtils
  } = normalizeIntegration(integration);
  const utils = { ...defaultUtils,
    ...intUtils,
    ...overrides
  };
  const path = utils.resolvePath('', basePath);

  if (path === undefined) {
    throw new Error(`${basePath} is not a valid base path`);
  } else if (path && !source().value) {
    setSource({
      value: path,
      mode: 'init'
    });
  }

  const baseRoute = createRouteState(utils, path, path, false, () => [path, {}]);
  const referrers = [];
  const [isRouting, start] = solidJs.useTransition();
  const [reference, setReference] = solidJs.createSignal(source().value, true);
  const [location] = solidJs.createState({
    get path() {
      return reference().split('?', 1)[0];
    },

    get queryString() {
      return reference().split('?', 2)[1] || '';
    }

  });

  function redirect(mode, to, options = {
    resolve: false
  }) {
    const currentRoute = solidJs.useContext(RouteContext) || baseRoute;
    const resolvedTo = options.resolve ? currentRoute.resolvePath(to) : utils.resolvePath('', to);

    if (resolvedTo === undefined) {
      throw new Error(`Path '${path}' is not a routable path`);
    }

    const redirectCount = referrers.push({
      ref: solidJs.untrack(reference),
      mode
    });

    if (redirectCount > MAX_REDIRECTS) {
      throw new Error('Too many redirects');
    }

    start(() => setReference(resolvedTo));
  }

  function handleRouteEnd(nextRef) {
    const first = referrers.shift();

    if (first) {
      if (nextRef !== first.ref) {
        setSource({
          value: nextRef,
          mode: first.mode
        });
      }

      referrers.length = 0;
    }
  }

  solidJs.createRenderEffect(() => {
    start(() => setReference(source().value));
  });
  solidJs.createRenderEffect(() => {
    handleRouteEnd(reference());
  });
  return {
    base: baseRoute,
    location,
    query: createMapMemo(() => location.queryString ? utils.parseQuery(location.queryString) : {}),
    isRouting,
    utils,

    push(to, options) {
      redirect('push', to, options);
    },

    replace(to, options) {
      redirect('replace', to, options);
    }

  };
}
function createRoute(pattern = '', end = false) {
  const router = useRouter();
  const parent = useRoute();
  const path = parent.resolvePath(pattern);

  if (path === undefined) {
    throw new Error(`${pattern} is not a valid path`);
  }

  if (parent.end && !end) {
    throw new Error(`Route '${path}' parent is a terminal route`);
  }

  const matcher = router.utils.createMatcher(path, {
    end
  });
  const match = solidJs.createMemo(() => matcher(router.location.path));
  return createRouteState(router.utils, router.base.path, path, end, match);
}
function createRouteState(utils, basePath, path, end, matchSignal) {
  const match = solidJs.createMemo(() => {
    const routeMatch = matchSignal();
    return routeMatch ? routeMatch[0] : undefined;
  }, undefined, true);
  return {
    path,
    end,
    match,
    params: createMapMemo(() => {
      const routeMatch = matchSignal();
      return routeMatch ? routeMatch[1] : {};
    }),

    resolvePath(path) {
      const matchPath = match();
      return matchPath !== undefined ? utils.resolvePath(basePath, path, matchPath) : undefined;
    }

  };
}

function createMapMemo(fn) {
  const map = solidJs.createMemo(fn, undefined, true);
  const data = solidJs.createMemo(map, undefined, (a, b) => {
    solidJs.reconcile(b, {
      key: null
    })(a);
    return true;
  });
  const [state] = solidJs.createState({
    get map() {
      return data();
    }

  });
  return new Proxy({}, {
    get(_, key) {
      return solidJs.untrack(() => state.map)[key];
    },

    ownKeys() {
      return Reflect.ownKeys(map());
    },

    getOwnPropertyDescriptor() {
      return {
        enumerable: true,
        configurable: true
      };
    }

  });
}

const _tmpl$ = web.template(`<a></a>`, 2);

function callEventHandlerUnion(handler, evt) {
  if (typeof handler === 'function') {
    handler(evt);
  } else if (handler) {
    handler[0](handler[1], evt);
  }
}

function LinkBase(props) {
  const [, rest] = solidJs.splitProps(props, ['children', 'to', 'href', 'onClick']);
  const router = useRouter();
  const href = solidJs.createMemo(() => props.to !== undefined ? router.utils.renderPath(props.to) : props.href);

  function handleClick(evt) {
    callEventHandlerUnion(props.onClick, evt);

    if (props.to !== undefined && !evt.defaultPrevented && evt.button === 0 && (!props.target || props.target === '_self') && !(evt.metaKey || evt.altKey || evt.ctrlKey || evt.shiftKey)) {
      evt.preventDefault();
      router.push(props.to, {
        resolve: false
      });
    }
  }

  return (() => {
    const _el$ = _tmpl$.cloneNode(true);

    _el$.$$click = handleClick;

    web.spread(_el$, rest, false, true);

    web.insert(_el$, () => props.children);

    web.effect(() => web.setAttribute(_el$, "href", href()));

    return _el$;
  })();
}

function Link(props) {
  const route = useRoute();
  const to = solidJs.createMemo(() => props.noResolve ? props.href : route.resolvePath(props.href));
  return web.createComponent(LinkBase, web.mergeProps(props, {
    get to() {
      return to();
    }

  }));
}
function NavLink(props) {
  props = solidJs.mergeProps({
    activeClass: 'is-active'
  }, props);
  const [, rest] = solidJs.splitProps(props, ['activeClass', 'end']);
  const router = useRouter();
  const route = useRoute();
  const to = solidJs.createMemo(() => props.noResolve ? props.href : route.resolvePath(props.href));
  const matcher = solidJs.createMemo(() => {
    const path = to();
    return path !== undefined ? router.utils.createMatcher(path, {
      end: !!props.end
    }) : undefined;
  });
  const isActive = solidJs.createMemo(() => {
    const m = matcher();
    return m && !!m(router.location.path);
  }, false, true);
  return web.createComponent(LinkBase, web.mergeProps(rest, {
    get to() {
      return to();
    },

    get classList() {
      return {
        [props.activeClass]: isActive()
      };
    },

    get ["aria-current"]() {
      return isActive() ? 'page' : undefined;
    }

  }));
}
function Redirect(props) {
  const router = useRouter();
  const href = props.href;
  const path = typeof href === 'function' ? href(router) : href;
  router.replace(path, {
    resolve: !props.noResolve
  });
  return null;
}
function MatchRoute(props) {
  return web.createComponent(Route, web.mergeProps(props, {
    component: solidJs.Match
  }));
}

function renderChildren(props, args) {
  const children = props.children;
  return typeof children === 'function' && children.length ? solidJs.untrack(() => children(...args)) : children;
}

function Route(props) {
  const {
    path,
    end,
    component: Comp = solidJs.Show
  } = props;
  const router = useRouter();
  const route = createRoute(path, end);
  return web.createComponent(Comp, {
    get when() {
      return route.match() !== undefined;
    },

    get children() {
      return web.createComponent(RouteContext.Provider, {
        value: route,

        get children() {
          return renderChildren(props, [route, router]);
        }

      });
    }

  });
}
function Router(props) {
  const {
    integration,
    basePath,
    utils
  } = props;
  const router = createRouter(integration, basePath, utils);
  return web.createComponent(RouterContext.Provider, {
    value: router,

    get children() {
      return props.children;
    }

  });
}

web.delegateEvents(["click"]);

function bindEvent(target, type, handler) {
  target.addEventListener(type, handler);
  return () => target.removeEventListener(type, handler);
}

function intercept(signal, get, set) {
  const [value, setValue] = signal;
  return [get ? () => get(value()) : value, set ? v => setValue(set(v)) : setValue];
}

function createIntegration(get, set, init, utils) {
  const signal = intercept(solidJs.createSignal({
    value: get()
  }, (a, b) => a.value === b.value), undefined, next => {
    const {
      value,
      mode
    } = next;
    mode && set(value, mode);
    return next;
  });
  init && solidJs.onCleanup(init((value = get()) => {
    signal[1]({
      value
    });
  }));
  return {
    signal,
    utils
  };
}
function pathIntegration() {
  return createIntegration(() => window.location.pathname + window.location.search, (value, mode) => {
    if (mode === 'push') {
      window.history.pushState(null, '', value);
    } else {
      window.history.replaceState(null, '', value);
    }
  }, notify => bindEvent(window, 'popstate', () => notify()));
}
function hashIntegration() {
  return createIntegration(() => window.location.hash.slice(1), value => {
    window.location.hash = value;
  }, notify => bindEvent(window, 'hashchange', () => notify()), {
    renderPath: path => `#${path}`
  });
}
function historyIntegration(history) {
  return createIntegration(() => history.location.pathname + history.location.search, (value, mode) => {
    if (mode === 'push') {
      history.push(value);
    } else {
      history.replace(value);
    }
  }, notify => history.listen(evt => {
    if (evt.action === 'POP') {
      notify();
    }
  }), {
    renderPath: history.createHref
  });
}

exports.Link = Link;
exports.MatchRoute = MatchRoute;
exports.NavLink = NavLink;
exports.Redirect = Redirect;
exports.Route = Route;
exports.Router = Router;
exports.createIntegration = createIntegration;
exports.createMatcher = createMatcher;
exports.createRoute = createRoute;
exports.createRouter = createRouter;
exports.hashIntegration = hashIntegration;
exports.historyIntegration = historyIntegration;
exports.parseQuery = parseQuery;
exports.pathIntegration = pathIntegration;
exports.renderPath = renderPath;
exports.resolvePath = resolvePath;
exports.useRoute = useRoute;
exports.useRouter = useRouter;
